import { z } from 'zod';
import { ClientInfo, ClientKind, ClientPlatform, Email, Id, Timestamp, singleLine } from './common';
import { NoContent, endpoint } from './endpoint';
import { lenient, lenientArray } from './lenient';
import { InstanceState } from './server-info';
import { Me } from './users';

/**
 * Password strength 0–4, shared by the invite form meter and the server check
 * (server requires ≥ 2): ≥8 chars, ≥12 chars, letters+digits, a symbol.
 */
export function passwordScore(password: string): number {
  let score = 0;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[0-9]/.test(password) && /[a-zA-Z가-힣]/.test(password)) score += 1;
  if (/[^0-9a-zA-Z가-힣]/.test(password)) score += 1;
  return Math.min(4, score);
}
export const MIN_PASSWORD_SCORE = 2;

export const NewPassword = z
  .string()
  .min(8)
  .max(256)
  .refine((value) => passwordScore(value) >= MIN_PASSWORD_SCORE, 'password is too weak');

export const TotpCode = z.string().regex(/^\d{6}$/);
export const RecoveryCode = z.string().trim().regex(/^[A-Z0-9]{4}-?[A-Z0-9]{4}-?[A-Z0-9]{4}$/i);

/** Desktop credentials. The refresh token is kept by the Electron main process only. */
export const DesktopTokens = z.object({
  accessToken: z.string(),
  accessTokenExpiresAt: Timestamp,
  refreshToken: z.string(),
  refreshTokenExpiresAt: Timestamp,
});
export type DesktopTokens = z.infer<typeof DesktopTokens>;

export const SessionInfo = z.object({
  id: Id,
  kind: lenient(ClientKind, 'web'),
  expiresAt: Timestamp,
});

export const LoginOk = z.object({
  status: z.literal('ok'),
  me: Me,
  session: SessionInfo,
  /** Web only: value for the `x-sd-csrf` header (also set as the readable `sd_csrf` cookie). */
  csrfToken: z.string().optional(),
  /** Desktop only. */
  tokens: DesktopTokens.optional(),
  /** Only right after MFA enrollment; shown once. */
  recoveryCodes: z.array(z.string()).optional(),
});
export type LoginOk = z.infer<typeof LoginOk>;

/** A login step added by a newer server reads as `unsupported`: the app asks for an update. */
export const LoginResult = lenient(
  z.discriminatedUnion('status', [
    LoginOk,
    z.object({
      status: z.literal('mfa_required'),
      /** Short-lived (5 min) challenge bound to this login attempt. */
      challengeToken: z.string(),
      methods: lenientArray(z.enum(['totp', 'recovery_code'])),
    }),
    z.object({
      /** Owners/admins must enroll TOTP before their first session. */
      status: z.literal('mfa_setup_required'),
      challengeToken: z.string(),
    }),
    z.object({ status: z.literal('unsupported') }),
  ]),
  { status: 'unsupported' },
);
export type LoginResult = z.infer<typeof LoginResult>;

export const DeviceSession = z.object({
  id: Id,
  kind: lenient(ClientKind, 'web'),
  platform: lenient(ClientPlatform, 'web'),
  deviceLabel: z.string(),
  createdAt: Timestamp,
  lastUsedAt: Timestamp,
  current: z.boolean(),
});
export type DeviceSession = z.infer<typeof DeviceSession>;

export const SetupStep = z.enum(['owner', 'verify_email', 'mfa', 'done']);

export const authEndpoints = {
  login: endpoint({
    id: 'auth.login',
    method: 'POST',
    path: '/api/v1/auth/login',
    auth: 'public',
    body: z.object({ email: z.string().max(254), password: z.string().min(1).max(256), client: ClientInfo }),
    response: LoginResult,
    rateLimit: 'auth',
  }),
  /** 10 wrong codes for one account within 15 minutes lock verification for 15 minutes (RATE_LIMITED + retry-after). */
  verifyMfa: endpoint({
    id: 'auth.verifyMfa',
    method: 'POST',
    path: '/api/v1/auth/mfa/verify',
    auth: 'public',
    body: z
      .object({ challengeToken: z.string().min(1), code: TotpCode.optional(), recoveryCode: RecoveryCode.optional() })
      .refine((v) => Boolean(v.code) !== Boolean(v.recoveryCode), 'provide exactly one of code or recoveryCode'),
    response: LoginOk,
    rateLimit: 'auth',
  }),
  startMfaSetup: endpoint({
    id: 'auth.startMfaSetup',
    method: 'POST',
    path: '/api/v1/auth/mfa/setup',
    auth: 'public',
    body: z.object({ challengeToken: z.string().min(1) }),
    response: z.object({ secret: z.string(), otpauthUrl: z.string() }),
    rateLimit: 'auth',
  }),
  confirmMfaSetup: endpoint({
    id: 'auth.confirmMfaSetup',
    method: 'POST',
    path: '/api/v1/auth/mfa/setup/confirm',
    auth: 'public',
    body: z.object({ challengeToken: z.string().min(1), code: TotpCode }),
    response: LoginOk,
    rateLimit: 'auth',
  }),
  /**
   * Desktop refresh-token rotation. Retrying with the previous token within a
   * minute, before the new one was used, returns a fresh pair (lost response);
   * any other reuse of a rotated token revokes the session.
   */
  refresh: endpoint({
    id: 'auth.refresh',
    method: 'POST',
    path: '/api/v1/auth/refresh',
    auth: 'public',
    /** `appVersion` lets the server refuse apps older than its minimum and keeps the device list current. */
    body: z.object({ refreshToken: z.string().min(1), appVersion: z.string().max(32).optional() }),
    response: z.object({ tokens: DesktopTokens }),
    rateLimit: 'refresh',
  }),
  logout: endpoint({ id: 'auth.logout', method: 'POST', path: '/api/v1/auth/logout', auth: 'session', response: NoContent }),
  sessions: endpoint({
    id: 'auth.sessions',
    method: 'GET',
    path: '/api/v1/auth/sessions',
    auth: 'session',
    response: z.object({ sessions: z.array(DeviceSession) }),
  }),
  revokeSession: endpoint({
    id: 'auth.revokeSession',
    method: 'DELETE',
    path: '/api/v1/auth/sessions/:sessionId',
    auth: 'session',
    params: z.object({ sessionId: Id }),
    response: NoContent,
  }),
  revokeOtherSessions: endpoint({
    id: 'auth.revokeOtherSessions',
    method: 'POST',
    path: '/api/v1/auth/sessions/revoke-others',
    auth: 'session',
    response: z.object({ revoked: z.number().int().nonnegative() }),
  }),
  /**
   * Always 202 so the endpoint does not reveal which emails exist. Mail goes through the server's mail
   * provider; a server that sends no mail (`mail.status` not_configured) sends nothing, and a company
   * admin hands out the link instead (`admin.passwordResetLink`).
   */
  requestPasswordReset: endpoint({
    id: 'auth.requestPasswordReset',
    method: 'POST',
    path: '/api/v1/auth/password-reset',
    auth: 'public',
    body: z.object({ email: z.string().max(254) }),
    response: z.object({ accepted: z.literal(true) }),
    successStatus: 202,
    rateLimit: 'auth',
  }),
  /** Token comes from the URL fragment of the reset link (never sent in a GET). Revokes all sessions. */
  confirmPasswordReset: endpoint({
    id: 'auth.confirmPasswordReset',
    method: 'POST',
    path: '/api/v1/auth/password-reset/confirm',
    auth: 'public',
    body: z.object({ token: z.string().min(16).max(256), newPassword: NewPassword }),
    response: NoContent,
    rateLimit: 'auth',
  }),
};

/** Protected first-owner bootstrap (R09). Requires the one-time local bootstrap token printed by the installer. */
export const setupEndpoints = {
  /** Public. Once the instance is ACTIVE (or in maintenance) only `state` is filled: step `done`, the rest null. */
  status: endpoint({
    id: 'setup.status',
    method: 'GET',
    path: '/api/v1/setup/status',
    auth: 'public',
    response: z.object({
      state: InstanceState,
      step: SetupStep,
      companyName: z.string().nullable(),
      ownerEmailMasked: z.string().nullable(),
      /**
       * False when the server sends no mail: setup skips the emailed code, since the setup token
       * already proves control of the VM. Only while setup runs; absent on older servers (true).
       */
      emailCode: z.boolean().optional(),
    }),
  }),
  owner: endpoint({
    id: 'setup.owner',
    method: 'POST',
    path: '/api/v1/setup/owner',
    auth: 'public',
    body: z.object({
      bootstrapToken: z.string().min(16),
      companyName: singleLine({ min: 1, max: 60 }),
      ownerEmail: Email,
      ownerName: singleLine({ min: 1, max: 40 }),
      password: NewPassword,
    }),
    /** Step `mfa` with the authenticator-setup challenge when the server sends no mail (no email code to confirm). */
    response: z.object({ step: SetupStep, challengeToken: z.string().optional() }),
    rateLimit: 'auth',
  }),
  verifyEmail: endpoint({
    id: 'setup.verifyEmail',
    method: 'POST',
    path: '/api/v1/setup/verify-email',
    auth: 'public',
    body: z.object({ bootstrapToken: z.string().min(16), code: z.string().regex(/^\d{6}$/) }),
    response: z.object({ step: SetupStep, challengeToken: z.string() }),
    rateLimit: 'auth',
  }),
  /**
   * After the owner email is verified (or the owner is saved, on a server that
   * sends no mail): a fresh authenticator-setup challenge when the previous one
   * expired, ran out of attempts or the page was reloaded. Earlier setup
   * challenges stop working.
   */
  resume: endpoint({
    id: 'setup.resume',
    method: 'POST',
    path: '/api/v1/setup/resume',
    auth: 'public',
    body: z.object({ bootstrapToken: z.string().min(16) }),
    response: z.object({ step: SetupStep, challengeToken: z.string() }),
    rateLimit: 'auth',
  }),
};
